/**
 * A variação contra o período anterior (R02, p.31).
 *
 * O manual é explícito em duas coisas, e as duas estão aqui:
 *
 *  1. **Variação SÓ quando existe período de comparação real.** Sem base, o
 *     card não mostra variação nenhuma — não mostra "—", não mostra "0%", não
 *     mostra "+100%". Um "+100%" sobre base zero parece medida e é só a base
 *     faltando.
 *  2. **Quem diz o sentido é a tela**: para custo e tempo parado, subir é
 *     piorar. O `sentido` não se deriva do número; ele vem do que o indicador
 *     significa, e por isso é parâmetro.
 *
 * ## A JANELA não é calculada aqui
 *
 * Quem decide o período anterior é o servidor, que consulta as duas janelas e
 * diz na resposta de que período a comparação é
 * (`backend/src/gestor/periodo-anterior.ts`). A tela lê `anterior.de` e
 * `anterior.ate` do que chegou. Duas contas de "mesma duração" divergiriam, e
 * a variação passaria a comparar janelas de tamanhos diferentes — número
 * plausível e errado, que é o pior tipo.
 *
 * ## Por que a base zero não vira variação
 *
 * O caso é real: a empresa não lançou custo no mês passado e lançou neste.
 * A conta daria divisão por zero, e qualquer número escolhido para ocupar o
 * lugar ("+100%", "novo") seria invenção com cara de medida. O cartão então
 * diz só o valor, e o contexto já informa o período.
 */

/** Para onde o indicador foi, e se isso é bom. */
export type SentidoDoIndicador = 'subir_e_pior' | 'subir_e_melhor';

export interface VariacaoDoCard {
  /** O texto do card, já com o sinal: "12% vs. período anterior". */
  readonly variacao: string;
  readonly sentido: 'melhorou' | 'piorou';
}

/**
 * A variação entre dois períodos, pronta para o card.
 *
 * Devolve `null` quando não há comparação honesta: valor ausente dos dois
 * lados, base zero, ou variação que arredonda para zero — "0%" ocuparia a
 * linha da variação sem dizer nada, e a seta apontaria para algum lado.
 */
export function variacaoDoPeriodo(
  atual: number | null | undefined,
  anterior: number | null | undefined,
  sentido: SentidoDoIndicador,
): VariacaoDoCard | null {
  if (atual === null || atual === undefined) return null;
  if (anterior === null || anterior === undefined) return null;
  if (anterior === 0) return null;

  const porcento = ((atual - anterior) / Math.abs(anterior)) * 100;
  /* Uma casa decimal seria precisão falsa num número que já sai de duas somas
     arredondadas; e inteiro é como o exemplo do manual escreve. */
  const arredondado = Math.round(porcento);
  if (arredondado === 0) return null;

  const subiu = arredondado > 0;
  const piorou = sentido === 'subir_e_pior' ? subiu : !subiu;
  return {
    /* O SINAL É O QUE A SETA LÊ (`CardIndicador.variacaoSobe`): texto que
       começa com "-" desce. Por isso o número vai com sinal e o "vs." depois,
       e não "queda de 3%" — a seta não saberia ler a frase. */
    variacao: `${subiu ? '' : '-'}${String(Math.abs(arredondado))}% vs. período anterior`,
    sentido: piorou ? 'piorou' : 'melhorou',
  };
}
